<script>
  // One Binder Update rendered as a block: a binder header (owner/name, linking
  // to the live binder) plus its top-N Card Changes, with a "+N more" affordance
  // that links to the detail view. The genesis batch is shown as a normal update.
  import { navigate } from '../../lib/stores/router.svelte.js';
  import { topChanges } from '../../lib/logic/updates-logic.js';
  import CardChangeRow from './CardChangeRow.svelte';

  let { update, changes = null, topN = 10, headerOnly = false } = $props();

  // Three modes:
  //   headerOnly  → just the binder header + counts (the detail view owns the
  //                 grouped change sections, so the block must not also list
  //                 rows — that double-rendered every card).
  //   changes set → preview up to topN rows with a "+N more" affordance.
  //   neither     → count summary header + a "view all" link (the feed).
  const preview = $derived(!headerOnly && changes ? topChanges(changes, topN) : null);

  function openBinder() {
    if (update.binder_url) {
      navigate('binder', { binderUrl: update.binder_url, shortPath: update.binder_url });
    }
  }

  function openDetail() {
    // An Update belongs to its binder, so its detail nests under it:
    // /{short}/updates/{seq}. The client-facing identity is the per-binder seq
    // (the internal UUID id is never put in the URL).
    navigate('updates', { binder: update.binder_url, seq: update.seq });
  }

  // Deterministic accent color from the binder URL (same idea as the Discord
  // Binder Excerpt accent). Returns an HSL hue 0–359.
  function accentHue(url) {
    let hash = 0;
    const s = url || update.id || '';
    for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
    return Math.abs(hash) % 360;
  }

  const hue = $derived(accentHue(update.binder_url));
  // Accent colors: mid-saturation to stay warm and on-brand
  const accentSpine = $derived(`hsl(${hue}, 55%, 52%)`);
  const accentBg = $derived(`hsl(${hue}, 60%, 96%)`);
  const accentText = $derived(`hsl(${hue}, 50%, 34%)`);
</script>

<article
  class="update-block"
  class:update-block--header-only={headerOnly}
  style="--accent-spine: {accentSpine}; --accent-bg: {accentBg}; --accent-text: {accentText};"
>
  <!-- Left spine: the per-binder color accent, mimicking the binder's own spine -->
  <div class="binder-spine" aria-hidden="true"></div>

  <div class="block-body">
    <header class="update-header">
      <!-- Binder identity -->
      <button class="binder-identity" onclick={openBinder} title="Open binder">
        <span class="binder-name">{update.binder_name || update.binder_url || 'Binder'}</span>
        {#if update.owner_name}
          <span class="binder-owner">by {update.owner_name}</span>
        {/if}
      </button>

      <!-- Count chips: human labels, not diff symbols -->
      <div class="count-chips" aria-label="Change summary">
        {#if update.added > 0}
          <span class="chip chip--added">
            <span class="chip-dot"></span>
            {update.added} added
          </span>
        {/if}
        {#if update.removed > 0}
          <span class="chip chip--removed">
            <span class="chip-dot"></span>
            {update.removed} removed
          </span>
        {/if}
        {#if update.changed > 0}
          <span class="chip chip--changed">
            <span class="chip-dot"></span>
            {update.changed} updated
          </span>
        {/if}
      </div>
    </header>

    {#if headerOnly}
      <!-- Detail view: header-only. The grouped Added/Quantity/Removed sections
           below the block are the single source of change rows. -->
    {:else if preview}
      <div class="update-changes">
        {#each preview.preview as change (change.set_code + change.collector_number + change.finish + change.change_type)}
          <CardChangeRow {change} />
        {/each}
      </div>
      {#if preview.remaining > 0}
        <button class="view-more" onclick={openDetail}>
          +{preview.remaining} more cards
        </button>
      {/if}
    {:else}
      <button class="view-all" onclick={openDetail}>View all changes</button>
    {/if}
  </div>
</article>

<style>
  /* ── Card container ── */
  .update-block {
    display: flex;
    align-items: stretch;
    background: var(--color-bg-surface, #fff);
    border: 1px solid var(--color-border, #e6e0d5);
    border-radius: var(--radius-lg, 14px);
    margin-bottom: var(--space-sm, 8px);
    overflow: hidden;
    transition:
      box-shadow 0.18s ease,
      transform 0.18s ease;
    /* Subtle lift on hover to feel like a physical card */
    cursor: default;
  }

  .update-block:hover {
    box-shadow: 0 4px 16px rgba(44, 36, 25, 0.09);
    transform: translateY(-1px);
  }

  .update-block--header-only {
    margin-bottom: var(--space-md, 16px);
  }

  /* ── Per-binder color spine (leather metaphor) ── */
  .binder-spine {
    width: 5px;
    flex-shrink: 0;
    background: var(--accent-spine);
    border-radius: 0; /* flush with card corners via overflow:hidden on parent */
  }

  /* ── Block body ── */
  .block-body {
    flex: 1 1 auto;
    min-width: 0;
    padding: var(--space-md, 16px);
  }

  /* ── Header row ── */
  .update-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-sm, 8px);
    margin-bottom: var(--space-sm, 8px);
  }

  /* ── Binder identity button ── */
  .binder-identity {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-align: left;
    min-width: 0;
    flex: 1 1 0;
  }

  .binder-name {
    font-family: var(--font-serif, Georgia, serif);
    font-size: 1rem;
    font-weight: 700;
    color: var(--color-text, #2c2419);
    line-height: 1.2;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
    transition: color 0.12s;
  }

  .binder-identity:hover .binder-name {
    color: var(--accent-text);
  }

  .binder-owner {
    font-size: 0.775rem;
    color: var(--color-text-tertiary, #9a8e7c);
    margin-top: 1px;
    white-space: nowrap;
  }

  /* ── Count chips ── */
  .count-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    flex-shrink: 0;
    justify-content: flex-end;
    align-items: flex-start;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 20px;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    white-space: nowrap;
  }

  .chip-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .chip--added {
    background: #f0fdf4;
    color: #166534;
    border: 1px solid #bbf7d0;
  }

  .chip--added .chip-dot {
    background: #22c55e;
  }

  .chip--removed {
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fecaca;
  }

  .chip--removed .chip-dot {
    background: #ef4444;
  }

  .chip--changed {
    background: #fffbeb;
    color: #92400e;
    border: 1px solid #fde68a;
  }

  .chip--changed .chip-dot {
    background: #f59e0b;
  }

  /* ── Change preview rows ── */
  .update-changes {
    margin-top: 4px;
  }

  /* ── Footer links ── */
  .view-more,
  .view-all {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: var(--space-sm, 8px);
    background: none;
    border: none;
    padding: 4px 0;
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--color-text-secondary, #5a5040);
    letter-spacing: 0.01em;
    transition: color 0.12s;
  }

  .view-more:hover,
  .view-all:hover {
    color: var(--accent-text);
  }

  /* ── Mobile ── */
  @media (max-width: 768px) {
    .update-header {
      flex-wrap: wrap;
      gap: 6px;
    }

    .count-chips {
      justify-content: flex-start;
      width: 100%;
      order: 3;
    }

    .binder-identity {
      flex: 1 0 auto;
    }

    .chip {
      font-size: 0.7rem;
    }
  }
</style>
